import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {IntegrationWidget} from 'shells/integration-widget/integration-widget';

import type {GetSessionUser_loggedInUser_phoneIntegrations as PhoneIntegration} from 'nutshell-graphql-types';

import {getServiceTypeIconForConnectionWidgets} from '../../calls/helpers';

import {InitialButton} from '../integrations/initial-button';

import {useSetAircallIntegrationIsEnabledForInstance} from './graphql/hooks/use-set-aircall-integration-is-enabled-for-instance';
import {useSetAircallIntegrationIsEnabledForUser} from './graphql/hooks/use-set-aircall-integration-is-enabled-for-user';

type Props = {
    phoneIntegration: PhoneIntegration;
    sendErrorMessagesToParent: (messages: string[]) => void;
    setIsAddingIntegration: (isAddingIntegration: boolean) => void;
    history: RouterHistory;
    connectOnMount?: boolean;
};

export function AircallIntegrationWidget(props: Props) {
    const {
        phoneIntegration,
        sendErrorMessagesToParent,
        setIsAddingIntegration,
        history,
        connectOnMount,
    } = props;

    const {setAircallIntegrationIsEnabledForInstance} =
        useSetAircallIntegrationIsEnabledForInstance();

    const {setAircallIntegrationIsEnabledForUser} = useSetAircallIntegrationIsEnabledForUser();

    if (!phoneIntegration.integration) {
        return (
            <InitialButton
                initialText='Connect with Aircall'
                isFullWidth={true}
                authCheckUrl='/aircall/has-auth'
                windowFeatures={{
                    toolbar: 'no',
                    location: 'no',
                    width: 600,
                    height: 850,
                }}
                logo={getServiceTypeIconForConnectionWidgets(phoneIntegration.integrationType)}
                connectOnMount={connectOnMount}
                enable={() =>
                    setAircallIntegrationIsEnabledForInstance(true)
                        .then((response) => {
                            return {
                                data: response.data,
                            };
                        })
                        .catch(() => {
                            sendErrorMessagesToParent(['Something went wrong. Please try again.']);
                        })
                }
                onAuthStart={() => {
                    sendErrorMessagesToParent([]);
                    setIsAddingIntegration(true);
                }}
                onAuthSuccess={() => {
                    return setAircallIntegrationIsEnabledForUser(true)
                        .then(() => {
                            history.replace(
                                '/connect-phone-integration/sync-preferences?integrationType=AIRCALL'
                            );
                        })
                        .catch((error) => {
                            const errorMessage = safelyGetGraphQLErrorMessage(error);
                            sendErrorMessagesToParent([
                                errorMessage || 'Something went wrong. Please try again.',
                            ]);
                        })
                        .finally(() => {
                            setIsAddingIntegration(false);
                        });
                }}
                onAuthError={(errMsgs) => {
                    sendErrorMessagesToParent(errMsgs);
                    setIsAddingIntegration(false);
                }}
            />
        );
    } else if (phoneIntegration.integration && !phoneIntegration.isEnabledForUser) {
        return (
            <IntegrationWidget
                status='initial'
                initialText='Connect with Aircall'
                fullWidthInitialButton={true}
                account={null}
                logo={getServiceTypeIconForConnectionWidgets(phoneIntegration.integrationType)}
                connectOnMount={connectOnMount}
                onConnectClick={() => {
                    sendErrorMessagesToParent([]);
                    setIsAddingIntegration(true);
                    setAircallIntegrationIsEnabledForUser(true)
                        .then(() => {
                            history.replace(
                                '/connect-phone-integration/sync-preferences?integrationType=AIRCALL'
                            );
                        })
                        .catch((error) => {
                            const errorMessage = safelyGetGraphQLErrorMessage(error);
                            sendErrorMessagesToParent([
                                errorMessage || 'Something went wrong. Please try again.',
                            ]);
                        })
                        .finally(() => {
                            setIsAddingIntegration(false);
                        });
                }}
                onDisconnectClick={() => {}}
            />
        );
    } else {
        return (
            <DisabledSection>
                <IntegrationWidget
                    status='enabled'
                    account={null}
                    logo={getServiceTypeIconForConnectionWidgets(phoneIntegration.integrationType)}
                    statusMessage='Aircall is connected'
                    onConnectClick={() => {}}
                    onDisconnectClick={() => {}}
                />
            </DisabledSection>
        );
    }
}
